import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import LikeIcon from '@atlaskit/icon/glyph/like';
import { Inline, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { CodeReviewerFeedbackSentiment } from 'src/components/conversation/src/internal/analytics';

import messages from '../i18n/code-reviewer-feedback.i18n';

interface TooltipContentProps {
  sentiment: CodeReviewerFeedbackSentiment;
}
const TooltipContent = ({ sentiment }: TooltipContentProps) => {
  const { formatMessage } = useIntl();

  return (
    <Text size="small" color="color.text.inverse">
      {sentiment === 'positive'
        ? formatMessage(messages.goodResponseLabel)
        : formatMessage(messages.badResponseLabel)}
    </Text>
  );
};

interface ThumbsUpThumbsDownProps {
  sentiment: CodeReviewerFeedbackSentiment;
  handleFeedbackClick: (x: string) => void;
}

export const ThumbsUpThumbsDown = ({
  sentiment,
  handleFeedbackClick,
}: ThumbsUpThumbsDownProps) => {
  const { formatMessage } = useIntl();

  return (
    <Inline space="space.050" alignBlock="center">
      <FormattedMessage
        {...messages.requestcodeReviewerRating}
        values={{
          boldText: (
            <Text weight="bold">
              {formatMessage(messages.howDidCodeReviewerDoMessage)}
            </Text>
          ),
          normalText: (
            <Text>
              {formatMessage(messages.rateCodeReviewerCommentMessage)}
            </Text>
          ),
        }}
      />
      <Tooltip content={<TooltipContent sentiment="positive" />}>
        {tooltipProps => (
          <Flex
            alignItems="center"
            justifyContent="center"
            xcss={xcss({
              borderRadius: 'border.radius.100',
              backgroundColor:
                sentiment === 'positive'
                  ? 'color.background.neutral.subtle.pressed'
                  : 'color.background.neutral.subtle',
            })}
          >
            <IconButton
              {...tooltipProps}
              icon={iconProps => (
                <LikeIcon
                  {...iconProps}
                  size="small"
                  primaryColor={token('color.icon.subtle')}
                />
              )}
              label={formatMessage(messages.goodResponseLabel)}
              spacing="compact"
              appearance="subtle"
              onClick={() => handleFeedbackClick('positive')}
              testId="thumbs-up"
            />
          </Flex>
        )}
      </Tooltip>
      <Tooltip content={<TooltipContent sentiment="negative" />}>
        {tooltipProps => (
          <Flex
            alignItems="center"
            justifyContent="center"
            xcss={xcss({
              borderRadius: 'border.radius.100',
              backgroundColor:
                sentiment === 'negative'
                  ? 'color.background.neutral.subtle.pressed'
                  : 'color.background.neutral.subtle',
            })}
          >
            <IconButton
              {...tooltipProps}
              icon={iconProps => (
                // There is no Dislike icon so this Inline rotates the Like icon 180deg
                <Inline
                  {...iconProps}
                  xcss={xcss({ transform: 'rotate(180deg)' })}
                >
                  <LikeIcon
                    size="small"
                    primaryColor={token('color.icon.subtle')}
                    label=""
                  />
                </Inline>
              )}
              spacing="compact"
              label={formatMessage(messages.badResponseLabel)}
              appearance="subtle"
              onClick={() => handleFeedbackClick('negative')}
              testId="thumbs-down"
            />
          </Flex>
        )}
      </Tooltip>
    </Inline>
  );
};
